// src/services/api.js
import axios from 'axios';
import logger from '../utils/logger';
import { safeSetItem } from '../utils/safeStorage';

// Base API URL with proper prefix handling
const API_URL = `${process.env.REACT_APP_API_URL || ''}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add timeout settings for large data transfers
  timeout: 30000, // 30 seconds
  // Add retry and connection settings
  maxRedirects: 5,
  validateStatus: function (status) {
    return status < 500; // Resolve only if status is less than 500
  },
  // Add connection keep-alive
  withCredentials: true,
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      
      const tokenPreview = token.substring(0, 10) + '...';
      logger.debug(`API Request: ${config.method?.toUpperCase() || 'GET'} ${config.url} with token: ${tokenPreview}`);
    } else {
      logger.debug(`API Request: ${config.method?.toUpperCase() || 'GET'} ${config.url} without authentication token`);
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Retry function for network errors
const retryRequest = async (config, retries = 2) => {
  for (let i = 0; i < retries; i++) {
    try {
      // Wait before retry (exponential backoff)
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
      }
      return await axios(config);
    } catch (retryError) {
      if (i === retries - 1) throw retryError;
      console.warn(`Retry attempt ${i + 1} failed, trying again...`);
    }
  }
};

// Token refresh promise to prevent multiple simultaneous refresh attempts
let tokenRefreshPromise = null;
// Guard so we only fire the session-expired toast/redirect once per page load
let sessionExpiredHandled = false;

const handleSessionExpired = () => {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;

  tokenRefreshPromise = null;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');

  if (typeof window === 'undefined') return;

  const sessionExpiredEvent = new CustomEvent('sessionExpired', {
    detail: { message: 'Your session has expired. Please log in again.' }
  });
  window.dispatchEvent(sessionExpiredEvent);

  // Don't redirect if user is already on the login page
  if (window.location.pathname === '/login') return;

  // Preserve where the user was so we can return them after re-login
  const next = window.location.pathname + window.location.search;
  setTimeout(() => {
    window.location.href = `/login?next=${encodeURIComponent(next)}`;
  }, 800);
};

// Login + refresh endpoints legitimately return 401 on bad credentials / expired
// refresh tokens — don't trigger the global session-expired flow for those.
const LOGIN_ENDPOINT_RE = /\/(users\/login|token\/refresh|token\/?)\/?$/;

const handle401 = async (client, originalRequest) => {
  if (originalRequest._retry) {
    handleSessionExpired();
    return new Promise(() => {});
  }
  originalRequest._retry = true;

  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) throw new Error('No refresh token available');

    if (tokenRefreshPromise) {
      await tokenRefreshPromise;
      const newToken = localStorage.getItem('access_token');
      if (!newToken) throw new Error('Token refresh completed but no new token available');
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return client(originalRequest);
    }

    tokenRefreshPromise = (async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const response = await axios.post(`${baseUrl}/api/token/refresh/`, {
          refresh: refreshToken
        }, { headers: { 'Content-Type': 'application/json' } });

        if (!response.data?.access) throw new Error('Invalid refresh response');
        safeSetItem('access_token', response.data.access);
        if (response.data.refresh) {
          safeSetItem('refresh_token', response.data.refresh);
        }
        return response.data.access;
      } finally {
        tokenRefreshPromise = null;
      }
    })();

    const newAccessToken = await tokenRefreshPromise;
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    return client(originalRequest);

  } catch (refreshError) {
    handleSessionExpired();
    // Never-resolving promise prevents components from rendering error UI during redirect
    return new Promise(() => {});
  }
};

// Shared response interceptor: refreshes token on 401, falls back to login redirect.
// Attach to any axios instance that sends authenticated requests.
// Note: apiClient sets `validateStatus: status < 500`, so 401 arrives via the success
// path. We intercept it there and in the error path so both paths are covered.
export const attachAuthInterceptor = (client) => {
  client.interceptors.response.use(
    (response) => {
      const cfg = response.config || {};
      if (response.status === 401 && !LOGIN_ENDPOINT_RE.test(cfg.url || '')) {
        return handle401(client, cfg);
      }
      return response;
    },
    (error) => {
      const cfg = error.config || {};
      if (error.response?.status === 401 && !LOGIN_ENDPOINT_RE.test(cfg.url || '')) {
        return handle401(client, cfg);
      }
      return Promise.reject(error);
    }
  );
};

// Response interceptor for handling network errors (apiClient-only, runs before auth interceptor)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry server errors (5xx) - these need to be fixed on the backend
    if (error.response?.status >= 500) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Server error detected (5xx), not retrying:', {
          status: error.response.status,
          url: originalRequest?.url,
          data: error.response?.data
        });
      }
      return Promise.reject(error);
    }

    // Handle network/connection errors with retry (but not server errors)
    if (error.code === 'ECONNABORTED' ||
        error.code === 'NETWORK_ERROR' ||
        error.message?.includes('timeout') ||
        error.message?.includes('Network Error') ||
        !error.response) {

      console.warn('Network error detected, attempting retry...', {
        code: error.code,
        message: error.message,
        url: originalRequest?.url
      });

      if (!originalRequest._networkRetried) {
        originalRequest._networkRetried = true;
        try {
          return await retryRequest(originalRequest, 2);
        } catch (retryError) {
          console.error('Network retry failed:', retryError);
          return Promise.reject(new Error('Connection lost. Please check your internet connection and try again.'));
        }
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('API Error Response:', {
        status: error.response?.status,
        url: originalRequest?.url,
        method: originalRequest?.method,
        data: error.response?.data,
        code: error.code,
        message: error.message
      });
    }

    return Promise.reject(error);
  }
);

attachAuthInterceptor(apiClient);

export default apiClient;